window.theme = window.theme || {};

theme.LeadTime = (function () {
  var selectors = {
    leadTime: '[data-product-lead-time]',
    variantsJson: '[data-variant-json]',
    form: '.product-single__form',
    productSection: '[data-section-type="product"]',
    variantSelect: '[data-variant-input]',
    variantButtons: '.variant-input input[type="radio"]',
    inventoryJson: '#inventory-data-json'
  };

  function LeadTime(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');
    this.productId = container.getAttribute('data-product-id');
    this.initialized = false;

    this.leadTimeContainer = this.container.querySelector(selectors.leadTime);
    this.variantJson = this.container.querySelector(selectors.variantsJson);

    if (this.leadTimeContainer && this.variantJson) {
      try {
        this.variants = JSON.parse(this.variantJson.innerHTML);

        // Inventory JSON lives in this section (avoid document.getElementById when duplicate ids exist)
        const inventoryDataJson = this.container.querySelector(selectors.inventoryJson);
        if (inventoryDataJson) {
          try {
            const rawData = inventoryDataJson.innerHTML;
            this.inventoryData = JSON.parse(rawData);
          } catch (e) {
            console.error('Error parsing inventory data JSON:', e);
            this.inventoryData = {};
          }
        } else {
          console.warn('Inventory data element not found in DOM');
          this.inventoryData = {};
        }

        this.inStockLeadTime = this.leadTimeContainer.dataset.inStockLeadTime;
        this.outOfStockLeadTime = this.leadTimeContainer.dataset.outOfStockLeadTime;

        this.init();
      } catch (e) {
        console.error('Error initializing LeadTime:', e);
      }
    }
  }

  LeadTime.prototype = {
    init: function () {
      if (this.initialized) return;

      const currentVariant = this._getInitialVariant();
      if (currentVariant) {
        this.updateLeadTime(currentVariant);
      }

      // document-level event: only react if this variant belongs to this product section (variant ids are shop-unique)
      document.addEventListener('variant:change', (evt) => {
        if (!evt.detail || !evt.detail.variant) return;
        const vid = evt.detail.variant.id.toString();
        if (!this.inventoryData || !Object.prototype.hasOwnProperty.call(this.inventoryData, vid)) {
          return;
        }
        this.updateLeadTime(evt.detail.variant);
      });

      this.container.addEventListener('variantChange', (evt) => {
        if (!evt.detail || !evt.detail.variant) return;
        const vid = evt.detail.variant.id.toString();
        if (!this.inventoryData || !Object.prototype.hasOwnProperty.call(this.inventoryData, vid)) {
          return;
        }
        this.updateLeadTime(evt.detail.variant);
      });

      // Listen for SKU change event (theme's most reliable variant event)
      this.container.addEventListener('variantSKUChange', (evt) => {
        setTimeout(() => {
          const skuEl = this.container.querySelector('[data-sku]');
          if (skuEl) {
            const sku = skuEl.textContent.trim();
            const variant = this.variants.find(v => v.sku === sku);
            if (variant) {
              this.updateLeadTime(variant);
            }
          }
        }, 50);
      });

      // Listen for button clicks
      const variantButtons = this.container.querySelectorAll(selectors.variantButtons);
      variantButtons.forEach(button => {
        button.addEventListener('change', () => {
          if (button.checked) {
            const optionValue = button.value;
            const optionIndex = button.getAttribute('data-index');
            const variant = this._findVariantByOption(optionIndex, optionValue);
            if (variant) {
              this.updateLeadTime(variant);
            }
          }
        });
      });

      this.initialized = true;
    },

    _findVariantById: function (variantId) {
      if (!this.variants || !variantId) return null;
      const id = variantId.toString();
      return this.variants.find(v => v.id.toString() === id);
    },

    _findVariantByOption: function (optionIndex, optionValue) {
      if (!this.variants || !optionIndex || !optionValue) return null;
      const index = parseInt(optionIndex.replace('option', '')) - 1;
      return this.variants.find(variant => {
        return variant.options && variant.options[index] === optionValue;
      });
    },

    _getInitialVariant: function () {
      if (!this.variants) return null;
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      if (variantId) {
        const fromUrl = this._findVariantById(variantId);
        if (fromUrl) return fromUrl;
      }
      const masterSelect = this.container.querySelector('select[name="id"]');
      if (masterSelect && masterSelect.value) {
        const fromDom = this._findVariantById(masterSelect.value);
        if (fromDom) return fromDom;
      }
      return this.variants[0];
    },

    /**
     * Syncs lead-time UI + hidden line-item properties for the given variant.
     * Not gated on init completion: the first call runs from init() before listeners attach.
     */
    updateLeadTime: function (variant) {
      if (!this.leadTimeContainer || !variant) {
        return;
      }

      const variantId = variant.id.toString();
      const inventory = this.inventoryData?.[variantId];

      if (!inventory) {
        console.warn('No inventory data found for variant:', variantId);
        return;
      }

      const rawQty = parseInt(inventory.inventory_quantity || 0, 10);
      const inventory_quantity = Number.isFinite(rawQty) ? Math.max(0, rawQty) : 0;
      const inventory_policy = inventory.inventory_policy || 'deny';
      const inventory_management = inventory.inventory_management || '';
      const continue_selling = inventory_policy === 'continue';
      const continueSellingProp = continue_selling ? 'continue' : 'deny';

      // Update hidden form fields
      const form = this.container.querySelector(selectors.form);
      if (form) {
        const inventoryField = form.querySelector('input[name="properties[_inventory_quantity]"]');
        const continueSellingField = form.querySelector('input[name="properties[_continue_selling]"]');
        
        if (inventoryField) {
          inventoryField.value = String(inventory_quantity);
        }
        if (continueSellingField) {
          continueSellingField.value = continueSellingProp;
        }
      }

      // Update display HTML
      let html = '';
      const labels = (window.theme && window.theme.strings && window.theme.strings.leadTime) || {};
      const unitLabel = (count) => {
        const n = parseInt(count || 0, 10);
        return n === 1 ? (labels.unitSingular || 'Unit') : (labels.unitPlural || 'Units');
      };
      if (inventory_management === 'shopify') {
        if (inventory_quantity > 0) {
          html = `
            <div class="product-lead-time__messages">
              <p class="product-lead-time__message product-lead-time__message--in-stock">
                <span class="icon icon--clock"></span>
                ${inventory_quantity} ${unitLabel(inventory_quantity)} ${(labels.availableFor || 'Available for')} ${(labels.quickShip || 'Quick Ship')} - ${(labels.shipsIn || 'Ships in')} ${this.inStockLeadTime}
              </p>
              <p class="product-lead-time__message product-lead-time__message--out-of-stock">
                <span class="icon icon--clock"></span>
                ${(labels.additionalUnits || 'Additional Units')} ${(labels.madeToOrder || 'Made to Order')} - ${(labels.shipsIn || 'Ships in')} ${this.outOfStockLeadTime}
              </p>
            </div>
          `;
        } else if (inventory_policy === 'continue') {
          html = `
            <div class="product-lead-time__messages">
              <p class="product-lead-time__message product-lead-time__message--out-of-stock">
                <span class="icon icon--clock"></span>
                ${(labels.additionalUnits || 'Additional Units')} ${(labels.madeToOrder || 'Made to Order')} - ${(labels.shipsIn || 'Ships in')} ${this.outOfStockLeadTime}
              </p>
            </div>
          `;
        }
      }
      
      this.leadTimeContainer.innerHTML = html;
    }
  };

  return LeadTime;
})();

document.addEventListener('DOMContentLoaded', function () {
  const productSections = document.querySelectorAll('[data-section-type="product"]');
  productSections.forEach(function (container) {
    new theme.LeadTime(container);
  });
});
